import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card, Col, DatePicker, Form, Modal, Row, Select, Spin, Table } from 'antd'
import { wtApiCall, wtApiPostWithContentTypeHandling } from 'src/helpers/commons/ApiWrapper'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtFormatMoney } from 'src/helpers/commons/utils'
import Pdf from './Pdf'

const { RangePicker } = DatePicker

const OutletExpenseReport = () => {
  const [form] = Form.useForm()

  const [isLoading, setIsLoading] = useState(false)
  const [outlets, setOutlets] = useState([])
  const [dateRange, setDateRange] = useState([])
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [selectedOutletIds, setSelectedOutletIds] = useState([])
  const [detailModalVisible, setDetailModalVisible] = useState(false)
  const [detailModalTitle, setDetailModalTitle] = useState('')
  const [detailModalRows, setDetailModalRows] = useState([])
  const [reportData, setReportData] = useState({
    config_name: '',
    outlets: [],
    rows: [],
    grand_total: 0,
  })

  const companyName = wtGetFromLocalStorage('company_name')

  useEffect(() => {
    wtApiCall(
      'outlets',
      { _page_size: 1000 },
      'get',
      (response) => {
        const data = response?.data?.data || []
        setOutlets(
          data.map((item) => ({
            value: item.id,
            label: item.name,
          })),
        )
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
      },
    )
  }, [])

  const validateFilters = () => {
    if (!fromDate || !toDate) {
      lzNotifyErrorMessageFromResult('Please select a date range!')
      return false
    }

    return true
  }

  const resetReport = () => {
    setReportData({
      config_name: '',
      outlets: [],
      rows: [],
      grand_total: 0,
    })
    setDetailModalVisible(false)
    setDetailModalTitle('')
    setDetailModalRows([])
  }

  const handleDateRangeChange = (values) => {
    setDateRange(values || [])
    setFromDate(values?.[0] ? values[0].format('YYYY-MM-DD') : null)
    setToDate(values?.[1] ? values[1].format('YYYY-MM-DD') : null)
    resetReport()
  }

  const handleOutletChange = (values) => {
    setSelectedOutletIds(values || [])
    resetReport()
  }

  const getReportData = () => {
    if (!validateFilters()) {
      return
    }

    setIsLoading(true)
    wtApiCall(
      'reports/outlet_expense_report',
      {
        from_date: fromDate,
        to_date: toDate,
        outlet_ids: selectedOutletIds,
      },
      'get',
      (response) => {
        setReportData(
          response?.data || {
            config_name: '',
            outlets: [],
            rows: [],
            grand_total: 0,
          },
        )
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const exportToExcel = async () => {
    if (!validateFilters()) {
      return
    }

    setIsLoading(true)
    await wtApiPostWithContentTypeHandling('reports/outlet_expense_report/export', {
      from_date: fromDate,
      to_date: toDate,
      outlet_ids: selectedOutletIds,
    })
    setIsLoading(false)
  }

  const print = async () => {
    if (!validateFilters()) {
      return
    }

    setIsLoading(true)
    Pdf(reportData, fromDate, toDate)
    setIsLoading(false)
  }

  const tableColumns = useMemo(() => {
    const openDetails = (record, outlet) => {
      const detailRows = record.cell_details?.[outlet.id] || []

      if (!Array.isArray(detailRows) || detailRows.length === 0) {
        return
      }

      setDetailModalTitle(`${record.description || 'Details'} - ${outlet.name}`)
      setDetailModalRows(detailRows)
      setDetailModalVisible(true)
    }

    const openTotalDetails = (record) => {
      const detailRows = Object.values(record.cell_details || {}).flat()

      if (!Array.isArray(detailRows) || detailRows.length === 0) {
        return
      }

      setDetailModalTitle(`${record.description || 'Details'} - Total`)
      setDetailModalRows(detailRows)
      setDetailModalVisible(true)
    }

    const outletColumns = (reportData.outlets || []).map((outlet) => ({
      title: outlet.name,
      dataIndex: `outlet_${outlet.id}`,
      key: `outlet_${outlet.id}`,
      align: 'right',
      width: 180,
      render: (value, record) => {
        const detailRows = record.cell_details?.[outlet.id] || []
        const hasDetails = Array.isArray(detailRows) && detailRows.length > 0

        return (
          <span
            style={{
              fontWeight: record.is_total ? 'bold' : 'normal',
              cursor: hasDetails ? 'pointer' : 'default',
              textDecoration: hasDetails ? 'underline' : 'none',
              color: hasDetails ? '#1677ff' : 'inherit',
            }}
            onClick={() => openDetails(record, outlet)}
          >
            {wtFormatMoney(value || 0, '', true, 2)}
          </span>
        )
      },
    }))

    return [
      {
        title: 'Sr',
        dataIndex: 'sr_no',
        key: 'sr_no',
        width: 70,
        fixed: 'left',
        align: 'center',
        render: (value, record) => (
          <span style={{ fontWeight: record.is_total ? 'bold' : 'normal' }}>{value}</span>
        ),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        width: 260,
        fixed: 'left',
        render: (value, record) => (
          <span style={{ fontWeight: record.is_total ? 'bold' : 'normal' }}>{value}</span>
        ),
      },
      ...outletColumns,
      {
        title: 'Total',
        dataIndex: 'total',
        key: 'total',
        width: 180,
        fixed: 'right',
        align: 'right',
        render: (value, record) => (
          <span
            style={{
              fontWeight: record.is_total ? 'bold' : 'normal',
              cursor:
                !record.is_total && Object.values(record.cell_details || {}).flat().length > 0
                  ? 'pointer'
                  : 'default',
              textDecoration:
                !record.is_total && Object.values(record.cell_details || {}).flat().length > 0
                  ? 'underline'
                  : 'none',
              color:
                !record.is_total && Object.values(record.cell_details || {}).flat().length > 0
                  ? '#1677ff'
                  : 'inherit',
            }}
            onClick={() => !record.is_total && openTotalDetails(record)}
          >
            {wtFormatMoney(value || 0, '', true, 2)}
          </span>
        ),
      },
    ]
  }, [reportData.outlets, reportData.rows])

  const tableData = useMemo(() => {
    const rows = (reportData.rows || []).map((row, index) => {
      const formattedRow = {
        key: `row-${index + 1}`,
        sr_no: index + 1,
        description: row.description,
        total: row.total || 0,
        is_total: false,
        cell_details: row.cell_details || {},
      }

      ;(reportData.outlets || []).forEach((outlet) => {
        const outletAmount = (row.outlet_amounts || []).find((item) => item.outlet_id === outlet.id)
        formattedRow[`outlet_${outlet.id}`] = outletAmount?.amount || 0
      })

      return formattedRow
    })

    if ((reportData.outlets || []).length > 0) {
      const totalRow = {
        key: 'total-row',
        sr_no: '',
        description: 'Total',
        total: reportData.grand_total || 0,
        is_total: true,
        cell_details: {},
      }

      ;(reportData.outlets || []).forEach((outlet) => {
        totalRow[`outlet_${outlet.id}`] = outlet.total || 0
      })

      rows.push(totalRow)
    }

    return rows
  }, [reportData])

  const detailColumns = useMemo(
    () => [
      {
        title: 'Account Code',
        dataIndex: 'account_code',
        key: 'account_code',
        width: 180,
      },
      {
        title: 'Account Name',
        dataIndex: 'account_name',
        key: 'account_name',
      },
      {
        title: 'Amount',
        dataIndex: 'amount',
        key: 'amount',
        width: 160,
        align: 'right',
        render: (value) => wtFormatMoney(value || 0, '', true, 2),
      },
    ],
    [],
  )

  return (
    <div
      className="container-fluid"
      style={{ padding: '1em', backgroundColor: 'white', minHeight: '100vh', fontFamily: 'Helvetica' }}
    >
      <Form layout="vertical" form={form} onFinish={getReportData} style={{ maxWidth: '100%', margin: '0 auto' }}>
        <Card
          title={
            <span style={{ fontSize: '22px' }}>{companyName} Outlet Expense Report</span>
          }
          style={{ width: '100%' }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} md={8}>
              <Form.Item label="Date Range" required>
                <RangePicker
                  style={{ width: '100%' }}
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={10}>
              <Form.Item label="Outlets">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="All outlets"
                  value={selectedOutletIds}
                  onChange={handleOutletChange}
                  options={outlets}
                  optionFilterProp="label"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={6} style={{ display: 'flex', alignItems: 'end', gap: 8 }}>
              <Button type="primary" htmlType="submit" disabled={isLoading}>
                Search
              </Button>
              <Button
                style={{ backgroundColor: '#4BB543', color: '#fff' }}
                onClick={exportToExcel}
                disabled={isLoading}
              >
                Export
              </Button>
              <Button
                style={{ backgroundColor: 'red', color: '#fff' }}
                onClick={print}
                disabled={isLoading}
              >
                Print
              </Button>
            </Col>
          </Row>

          <Table
            columns={tableColumns}
            dataSource={tableData}
            pagination={false}
            bordered
            size="small"
            scroll={{ x: 1200, y: 500 }}
            loading={{ indicator: <div><Spin /></div>, spinning: isLoading }}
          />
        </Card>
      </Form>

      <Modal
        title={detailModalTitle || 'Value Details'}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <Table
          columns={detailColumns}
          dataSource={detailModalRows.map((item, index) => ({
            key: `${item.account_id || 'account'}-${index}`,
            ...item,
          }))}
          pagination={false}
          size="small"
          bordered
          scroll={{ y: 360 }}
        />
      </Modal>
    </div>
  )
}

export default OutletExpenseReport
